import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "../components/LogoMark.js";
import { ProductIcon } from "../components/ProductIcon.js";
import { FeatureIcon } from "../components/FeatureIcon.js";
import { PRODUCTS } from "../config/products.js";
import { PricingSection } from "../components/PricingSection.js";
import { waitlistMailto } from "../lib/contactLinks.js";
import { termsUrl, privacyUrl } from "../lib/legalUrls.js";

// ── Design tokens v2 ───────────────────────────────────────────────────────
const C = {
  purple: "#351DBD",
  purpleDark: "#1a0f6e",
  orange: "#FF861D",
  blue: "#349DFF",
  purpleLight: "#EDE9FC",
  orangeLight: "#FFF0E2",
  blueLight: "#E5F2FF",
  navy: "#261F59",
  textStrong: "#2b2e44",
  textMuted: "#6b7084",
  textFaint: "#9096a8",
  muted: "#E3EBF6",
  mutedSoft: "#EEF1F7",
  bg: "#f0eef6",
  bgTint: "#F7F6FB",
  white: "#ffffff",
  success: "#22c55e",
  successLight: "#dcfce7",
  successText: "#166534",
} as const;

// ── Accent rotation: purple → blue → orange ────────────────────────────────
const ROTATION = [C.purple, C.blue, C.orange];
const ROTATION_LIGHT: Record<string, string> = {
  [C.purple]: C.purpleLight,
  [C.blue]: C.blueLight,
  [C.orange]: C.orangeLight,
};
function productAccent(rotSlot: number) {
  const accent = ROTATION[rotSlot % 3];
  return { accent, tint: ROTATION_LIGHT[accent] };
}

// Map products to rotation slots
const PRODUCT_SLOTS: Record<string, number> = {
  qr: 0, invoice: 1, planner: 2,
  "link-page": 0, menu: 1, events: 2,
};

const features = [
  { title: "Single sign-on",       desc: "One account across every product. Pay once, log in once.", icon: "shield" as const, tint: C.purpleLight, color: C.purple },
  { title: "One unified dashboard", desc: "Every tool, every invoice, every stat in one place.",      icon: "grid" as const,   tint: C.blueLight,   color: C.blue },
  { title: "Built for Europe",     desc: "EU data residency. Frankfurt servers. GDPR by default.",   icon: "globe" as const,  tint: C.orangeLight, color: C.orange },
];

// ── Reveal hook ────────────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px" },
    );
    el.querySelectorAll(".reveal").forEach((child) => obs.observe(child));
    return () => obs.disconnect();
  }, []);
  return ref;
}

// ═════════════════════════════════════════════════════════════════════════════
// PUBLIC LANDING PAGE — v2 design
// ═════════════════════════════════════════════════════════════════════════════
export function PublicLanding() {
  const revealRef = useReveal();
  // HD-102: derive coming-soon count from the product list rather than hardcoding "5".
  const comingSoonCount = PRODUCTS.filter((p) => p.status !== "live").length;
  const currentYear = new Date().getFullYear();
  const legalLinks = [
    { label: "Terms",    href: termsUrl()   },
    { label: "Privacy",  href: privacyUrl() },
    { label: "GDPR",     href: import.meta.env.VITE_GDPR_URL     },
    { label: "Security", href: import.meta.env.VITE_SECURITY_URL },
  ].filter((l): l is { label: string; href: string } => typeof l.href === "string" && l.href.length > 0);

  return (
    <div ref={revealRef}>
      {/* ── NAV (glass, 72px, sticky) ──────────────────────────────── */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: `1px solid ${C.muted}`,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 32px",
            height: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
          }}
        >
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none", color: C.navy, fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em" }}>
            <LogoMark color={C.purple} size={28} />
            <span>Gen Pro Hub</span>
          </Link>

          <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
            <a href="#products" style={{ color: C.textMuted, fontSize: 14, fontWeight: 500, textDecoration: "none" }}>Products</a>
            <a href="#features" style={{ color: C.textMuted, fontSize: 14, fontWeight: 500, textDecoration: "none" }}>Why Gen Pro</a>
            <a href="#pricing" style={{ color: C.textMuted, fontSize: 14, fontWeight: 500, textDecoration: "none" }}>Pricing</a>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Link to="/login" className="brand-btn brand-btn-ghost brand-btn-sm">Sign in</Link>
            <Link to="/signup" className="brand-btn brand-btn-primary brand-btn-sm">Get started</Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────────── */}
      <header
        style={{
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(180deg, ${C.bg} 0%, ${C.white} 100%)`,
          borderBottom: `1px solid ${C.muted}`,
        }}
      >
        {/* Decorative radial glow */}
        <div
          style={{
            position: "absolute",
            inset: "-80px 0 auto 0",
            height: 820,
            background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(53,29,189,0.09), transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 32px",
          }}
        >
          <div
            style={{
              position: "relative",
              padding: "112px 0",
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              gap: 64,
              alignItems: "center",
            }}
          >
            {/* Text column */}
            <div className="reveal">
              {/* Eyebrow pill */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "7px 14px 7px 10px",
                  background: C.white,
                  border: `1px solid ${C.muted}`,
                  borderRadius: 9999,
                  boxShadow: "var(--shadow-sm)",
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.navy,
                  marginBottom: 32,
                }}
              >
                <span className="live-dot" />
                <span>QR Generator is live</span>
                <span style={{ color: C.textFaint, fontWeight: 400 }}>·</span>
                <span style={{ color: C.textMuted, fontWeight: 500 }}>{comingSoonCount} more coming soon</span>
              </div>

              {/* Display heading */}
              <h1
                style={{
                  fontSize: "clamp(44px, 6vw, 60px)",
                  fontWeight: 800,
                  lineHeight: 1.04,
                  letterSpacing: "-0.035em",
                  color: C.navy,
                  margin: 0,
                }}
              >
                All your business tools.
                <br />
                <span style={{ color: C.purple }}>One login.</span> One bill.
              </h1>

              {/* Lead */}
              <p
                style={{
                  fontSize: 20,
                  fontWeight: 400,
                  lineHeight: 1.55,
                  letterSpacing: "-0.005em",
                  color: C.textStrong,
                  marginTop: 20,
                  maxWidth: "52ch",
                }}
              >
                Run QR codes, invoices, menus and events from the same dashboard — one login, one bill, EU data.
              </p>

              {/* CTAs */}
              <div style={{ display: "flex", gap: 12, marginTop: 40, flexWrap: "wrap" }}>
                <Link to="/signup" className="brand-btn brand-btn-primary brand-btn-lg">
                  Get started free
                </Link>
                <a href="#products" className="brand-btn brand-btn-secondary brand-btn-lg">
                  See products →
                </a>
              </div>

              {/* Proof row */}
              <div
                style={{
                  marginTop: 48,
                  display: "flex",
                  gap: 28,
                  alignItems: "center",
                  color: C.textFaint,
                  fontSize: 13,
                }}
              >
                <div><strong style={{ color: C.navy, fontWeight: 700 }}>Frankfurt</strong> servers, GDPR by default</div>
                <span style={{ width: 1, height: 18, background: C.muted }} />
                <div>No credit card · 2-min setup</div>
              </div>
            </div>

            {/* Prism ornament */}
            <div className="reveal" aria-hidden="true">
              <PrismOrnament />
            </div>
          </div>
        </div>
      </header>

      {/* ── PRODUCTS ───────────────────────────────────────────────── */}
      <section id="products" style={{ background: C.white, padding: "var(--section-pad) 0" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}>
          <SectionHead numeral="01" label="Products" className="reveal">
            <h2 style={{ fontSize: "clamp(30px, 3.4vw, 36px)", fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.02em", color: C.navy, margin: 0 }}>
              Six tools. <span style={{ color: C.purple }}>One account.</span>
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: C.textMuted, marginTop: 14, maxWidth: "52ch" }}>
              Buy the one you need today. Everything else is ready when you are — same login, same data, same bill.
            </p>
          </SectionHead>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {PRODUCTS.map((p) => {
              const slot = PRODUCT_SLOTS[p.id] ?? 0;
              const { accent, tint } = productAccent(slot);
              const isLive = p.status === "live" || p.status === "beta";

              return (
                <article
                  key={p.id}
                  className={`brand-card ${isLive ? "brand-card-interactive" : ""} reveal`}
                  style={{
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    opacity: isLive ? 1 : 0.88,
                    cursor: isLive ? "pointer" : "default",
                  }}
                  onClick={() => isLive && p.url && (window.location.href = p.url)}
                >
                  {/* Inline icon card style (no banner) */}
                  <div style={{ padding: 28 }}>
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: tint,
                        display: "grid",
                        placeItems: "center",
                        marginBottom: 16,
                      }}
                    >
                      <ProductIcon type={p.icon} color={accent} size={24} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <h3 style={{ fontSize: 17, fontWeight: 700, color: C.navy, margin: 0 }}>{p.name}</h3>
                      {p.status === "live" && (
                        <span className="brand-badge brand-badge-live">
                          <span className="live-dot" /> LIVE
                        </span>
                      )}
                      {p.status === "coming-soon" && (
                        <span className="brand-badge brand-badge-soon">SOON</span>
                      )}
                    </div>
                    <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.5, margin: 0, minHeight: 42 }}>
                      {p.description}
                    </p>
                    {isLive ? (
                      <span style={{ marginTop: 18, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: accent }}>
                        Open app
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                        </svg>
                      </span>
                    ) : (
                      <a
                        href={waitlistMailto(p.id, p.name)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textMuted, fontWeight: 500, borderBottom: `1px dashed ${C.muted}`, paddingBottom: 2, cursor: "pointer", textDecoration: "none" }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        Notify me at launch
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FEATURES ───────────────────────────────────────────────── */}
      <section id="features" style={{ background: C.bgTint, padding: "var(--section-pad) 0" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}>
          <SectionHead numeral="02" label="Built for operators" className="reveal">
            <h2 style={{ fontSize: "clamp(30px, 3.4vw, 36px)", fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.02em", color: C.navy, margin: 0 }}>
              One ecosystem, <span style={{ color: C.purple }}>not a stitched-together bundle.</span>
            </h2>
          </SectionHead>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 32 }}>
            {features.map((f, i) => (
              <div
                key={i}
                className="reveal"
                style={{
                  background: C.white,
                  borderRadius: 16,
                  padding: "36px 32px",
                  border: `1px solid ${C.muted}`,
                  boxShadow: "var(--shadow-sm)",
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: f.tint,
                    display: "grid",
                    placeItems: "center",
                    marginBottom: 24,
                  }}
                >
                  <FeatureIcon type={f.icon} color={f.color} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: C.navy, margin: "0 0 8px", letterSpacing: "-0.01em" }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ────────────────────────────────────────────────── */}
      <section id="pricing">
        <PricingSection />
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────────── */}
      <footer style={{ background: C.bgTint, borderTop: `1px solid ${C.muted}`, padding: "72px 0 40px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr",
              gap: 48,
              paddingBottom: 48,
              borderBottom: `1px solid ${C.muted}`,
            }}
          >
            {/* Brand column */}
            <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 340 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <MiniPrism />
                <span style={{ fontSize: 16, fontWeight: 800, color: C.navy, letterSpacing: "-0.02em" }}>Gen Pro Hub</span>
              </div>
              <p style={{ fontSize: 13, color: C.textMuted, margin: 0, lineHeight: 1.55 }}>
                Six self-serve tools for small businesses — under one login. Built and run in Europe.
              </p>
            </div>

            {/* Products column — links derived from PRODUCTS so additions to the
                catalog don't leave dead footer entries (HD-102). Live products link
                to productUrl(); coming-soon products use the HD-101 waitlist mailto. */}
            <FooterCol title="Products">
              {PRODUCTS.map((p) => {
                const isLive = p.status === "live" || p.status === "beta";
                const href = isLive
                  ? p.url
                  : waitlistMailto(p.id, p.name);
                return (
                  <FooterLink key={p.id} href={href} soon={!isLive}>
                    {p.name}
                  </FooterLink>
                );
              })}
            </FooterCol>

            {/* Company column — only links that resolve. Changelog / Roadmap
                pages don't exist yet, so they're omitted rather than dead-linked (HD-102). */}
            <FooterCol title="Company">
              <FooterLink href="#products">About the products</FooterLink>
            </FooterCol>

            {/* Legal column — URLs come from env so the marketing team can host the
                docs anywhere. Items with no URL configured are not rendered (HD-102). */}
            <FooterCol title="Legal">
              {legalLinks.map((l) => (
                <FooterLink key={l.label} href={l.href}>{l.label}</FooterLink>
              ))}
            </FooterCol>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 32,
              fontSize: 12,
              color: C.textFaint,
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <span>© {currentYear} Gen Pro Hub — Frankfurt, Germany</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <span className="live-dot" /> All systems operational
              <span style={{ width: 4, height: 4, borderRadius: "50%", background: C.textFaint }} />
              <span>v{__APP_VERSION__}</span>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════

function SectionHead({ numeral, label, children, className }: { numeral: string; label: string; children: React.ReactNode; className?: string }) {
  return (
    <div
      className={className}
      style={{
        display: "grid",
        gridTemplateColumns: "120px 1fr",
        gap: 48,
        alignItems: "baseline",
        marginBottom: 64,
      }}
    >
      <div
        style={{
          fontSize: 80,
          fontWeight: 800,
          lineHeight: 1,
          letterSpacing: "-0.04em",
          color: C.purpleLight,
          userSelect: "none",
        }}
      >
        {numeral}
      </div>
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase" as const,
            color: C.purple,
            marginBottom: 16,
          }}
        >
          {label}
        </div>
        {children}
      </div>
    </div>
  );
}

function PrismOrnament() {
  return (
    <div
      style={{
        position: "relative",
        width: 360,
        height: 360,
        filter: "drop-shadow(0 28px 60px rgba(53,29,189,0.18))",
      }}
    >
      <div
        style={{
          position: "absolute",
          width: 180,
          height: 220,
          top: 20,
          left: 20,
          background: C.navy,
          borderRadius: 28,
          boxShadow: "var(--shadow-lg)",
          transform: "rotate(-8deg)",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 190,
          height: 240,
          top: 50,
          left: 120,
          background: "linear-gradient(145deg, #349DFF 0%, #1298FF 60%, #3D1DBD 100%)",
          borderRadius: 28,
          boxShadow: "var(--shadow-lg)",
          transform: "rotate(4deg)",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 170,
          height: 200,
          top: 130,
          left: 70,
          background: `linear-gradient(135deg, ${C.purple} 0%, ${C.purpleDark} 100%)`,
          borderRadius: 28,
          boxShadow: "var(--shadow-lg)",
          transform: "rotate(-2deg)",
        }}
      />
      {/* Glass logo plaque */}
      <div
        style={{
          position: "absolute",
          right: -12,
          bottom: 10,
          width: 120,
          height: 120,
          background: "rgba(255,255,255,0.72)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.9)",
          borderRadius: 28,
          boxShadow: "var(--shadow-xl)",
          display: "grid",
          placeItems: "center",
          transform: "rotate(6deg)",
        }}
      >
        <LogoMark color={C.purple} size={50} />
      </div>
    </div>
  );
}

function MiniPrism() {
  return (
    <div style={{ width: 64, height: 64, position: "relative" }} aria-hidden="true">
      <div style={{ position: "absolute", width: 28, height: 36, top: 8, left: 4, background: C.navy, borderRadius: 14, transform: "rotate(-6deg)" }} />
      <div style={{ position: "absolute", width: 30, height: 40, top: 12, left: 24, background: "linear-gradient(145deg, #349DFF, #3D1DBD)", borderRadius: 14, transform: "rotate(4deg)" }} />
      <div style={{ position: "absolute", width: 26, height: 34, top: 22, left: 14, background: C.purple, borderRadius: 14, transform: "rotate(-2deg)" }} />
    </div>
  );
}

function FooterCol({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: C.navy, margin: "0 0 16px" }}>
        {title}
      </h4>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {children}
      </ul>
    </div>
  );
}

function FooterLink({ href, children, soon }: { href: string; children: React.ReactNode; soon?: boolean }) {
  return (
    <li>
      <a href={href} style={{ fontSize: 13, color: C.textMuted, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
        {children}
        {soon && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 999, background: C.muted, color: C.textFaint }}>
            SOON
          </span>
        )}
      </a>
    </li>
  );
}
